import type React from "react";

import { twResolve } from "@/helpers/tw-resolve";
import { isDefined } from "@/helpers/util";
import { Label, Pie, PieChart, type PieProps, Sector } from "@/ui-adapters/Recharts";
import { RuntimeMaxWidth } from "@/ui-adapters/RuntimeMaxWidth";

import type { ChartBaseProps } from "./types";

type RingChartProps = ChartBaseProps & {
  variant?: "full" | "half";
};

export function RingChart({
  variant = "full",
  value,
  max = 100,
  thresholds,
  renderValue = (value) => `${value}`,
}: RingChartProps): React.ReactNode {
  const chartData = [
    { name: "primary", value: value },
    {
      name: "remaining",
      value: max - value,
    },
  ];

  const commonProps: PieProps = {
    cx: "50%",
    cy: variant === "full" ? "50%" : "100%",
    startAngle: variant === "full" ? 450 : 180,
    endAngle: variant === "full" ? 90 : 0,
    outerRadius: variant === "full" ? "100%" : "200%",
    innerRadius: variant === "full" ? "90%" : "180%",
    cornerRadius: 99,
  };

  const displayValue = renderValue ? renderValue(value) : value;

  return (
    <RuntimeMaxWidth maxWidth={160}>
      {(widthAttributes) => (
        <PieChart
          {...widthAttributes}
          margin={{
            top: 0,
            right: 0,
            left: 0,
            bottom: 0,
          }}
          className={twResolve("aspect-square w-full", variant === "half" && "aspect-2/1")}
          responsive
        >
          {/* Background */}
          <Pie
            data={[{ name: "background", value: 100 }]}
            className="fill-border-default"
            isAnimationActive={false}
            stroke="none"
            tabIndex={-1}
            {...commonProps}
          />
          {/* Primary data */}
          <Pie
            data={chartData}
            dataKey="value"
            stroke="none"
            {...commonProps}
            shape={(props) => {
              const { name, ...sectorProps } = props as React.ComponentProps<typeof Sector> & { name: string };
              const isPrimary = name === "primary";

              return (
                <Sector
                  {...sectorProps}
                  className={twResolve(
                    isPrimary ? "fill-background-brand-secondary" : "fill-transparent",
                    isDefined(thresholds?.negative) &&
                      isPrimary &&
                      value <= thresholds.negative &&
                      "fill-background-danger",
                  )}
                  tabIndex={isPrimary ? 0 : -1}
                />
              );
            }}
          />
          {variant === "full" && (
            <Label position="center" className="fill-text-brand-secondary text-figure-lg tracking-tight">
              {displayValue}
            </Label>
          )}
          {variant === "half" && (
            <text x="50%" y="100%" dy={-8} className="fill-text-brand-secondary text-figure-lg" textAnchor="middle">
              {displayValue}
            </text>
          )}
        </PieChart>
      )}
    </RuntimeMaxWidth>
  );
}
